import { useCallback, useEffect, useState } from "react";
import { Link2, Copy, Trash2, Loader2, Plus, Mail } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const ACCESS = { none: "No access", listen: "Read-only (listen)", download: "Read-only + download" };
const STORAGE = [0, 1024, 5120, 10240];
const fmtMB = (mb) => (mb >= 1024 ? `${mb / 1024} GB` : `${mb} MB`);
const fmtDate = (d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const linkFor = (token) => `${window.location.origin}/login#invite=${token}`;
const EMAIL_OK = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const copy = async (text, msg) => {
  try { await navigator.clipboard.writeText(text); toast.success(msg); } catch { toast.error("Couldn't copy — select the link and copy it manually"); }
};

export const mailtoFor = (inv, senderName) => {
  const first = (inv.note || "").trim().split(/\s+/)[0];
  const perks = [
    inv.library_access !== "none" && `• ${inv.library_access === "download" ? "Listen to and download from" : "Listen to"} my shared library`,
    inv.upload_app && inv.storage_limit_mb ? `• Upload your own music (${fmtMB(inv.storage_limit_mb)} of Resonance storage)` : inv.upload_app && "• Upload your own music to Resonance storage",
    inv.upload_onedrive && "• Connect your own OneDrive",
    inv.upload_plex && "• Connect your own Plex server",
    inv.access_filejump && "• Listen to my FileJump collection",
  ].filter(Boolean).join("\n");
  const body = `Hi${first ? ` ${first}` : ""},

I've set up an account for you on Resonance Harbor, my personal media library.

Open this link to choose a password and sign in:
${linkFor(inv.token)}

The link is one-time and expires on ${new Date(inv.expires_at).toLocaleDateString(undefined, { month: "long", day: "numeric" })}.
${perks ? `\nWhat you can do:\n${perks}\n` : ""}
Resonance Harbor works in your browser — on your phone you can "Install as an app" from the settings to get it on your home screen with offline listening.

${senderName || "See you there"}`;
  return `mailto:${encodeURIComponent(inv.email || "")}?subject=${encodeURIComponent("Your invite to Resonance Harbor")}&body=${encodeURIComponent(body)}`;
};

const Field = ({ label, children, className = "" }) => (
  <label className={`block text-[11px] text-zinc-500 ${className}`}>{label}{children}</label>
);
const inputCls = "mt-1 block h-9 w-full rounded border border-zinc-200 bg-white px-2 text-sm text-zinc-900 outline-none focus:border-zinc-900";

const PermRow = ({ id, label, hint, checked, onChange, children }) => (
  <div className="flex items-center justify-between gap-3 py-2" data-testid={`invite-perm-${id}`}>
    <div className="min-w-0">
      <p className="text-xs text-zinc-800">{label}</p>
      {hint && <p className="text-[11px] text-zinc-400">{hint}</p>}
    </div>
    <div className="flex items-center gap-2">
      {children}
      <Switch checked={checked} onCheckedChange={onChange} className="scale-90" data-testid={`invite-${id}-switch`} />
    </div>
  </div>
);

const NewInvite = ({ onCreated, senderName }) => {
  const [f, setF] = useState({ note: "", email: "", library_access: "listen", upload_app: true, storage_limit_mb: 1024, upload_onedrive: true, upload_plex: false, access_filejump: false, expires_days: 7 });
  const [busy, setBusy] = useState("");
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const emailBad = f.email && !EMAIL_OK.test(f.email.trim());

  const create = async (sendMail) => {
    if (emailBad) { toast.error("That email address doesn't look right"); return; }
    if (sendMail && !f.email) { toast.error("Add the person's email address first"); return; }
    setBusy(sendMail ? "mail" : "copy");
    try {
      const { data } = await api.post("/admin/invites", { ...f, email: f.email.trim(), storage_limit_mb: f.upload_app ? Number(f.storage_limit_mb) || 0 : 0 });
      onCreated(data);
      if (sendMail) {
        window.location.href = mailtoFor(data, senderName);
        toast.success("Opening your mail app with the invite…");
      } else await copy(linkFor(data.token), "Invite link copied — send it to your guest");
      setF((x) => ({ ...x, note: "", email: "" }));
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  return (
    <div className="grid gap-5 rounded-md bg-zinc-50 p-4 md:grid-cols-[1fr_1fr]" data-testid="invite-form">
      <div className="space-y-3">
        <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-400">Who</p>
        <Field label="Name">
          <input value={f.note} onChange={(e) => set("note")(e.target.value)} placeholder="e.g. Aunt May" maxLength={80} data-testid="invite-note-input" className={inputCls} />
        </Field>
        <Field label="Email (where to send the invite)">
          <input value={f.email} onChange={(e) => set("email")(e.target.value)} placeholder="name@example.com" type="email" maxLength={120} data-testid="invite-email-input"
            className={`${inputCls} ${emailBad ? "border-red-400" : ""}`} />
          {emailBad && <span className="text-[10px] text-red-600" data-testid="invite-email-error">Enter a valid email address</span>}
        </Field>
        <Field label="Link expires in">
          <Select value={String(f.expires_days)} onValueChange={(v) => set("expires_days")(Number(v))}>
            <SelectTrigger className="mt-1 h-9 w-full bg-white text-xs" data-testid="invite-expires-select"><SelectValue /></SelectTrigger>
            <SelectContent>{[1, 3, 7, 30].map((d) => <SelectItem key={d} value={String(d)}>{d} day{d === 1 ? "" : "s"}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <div>
        <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-400">Permissions</p>
        <Field label="Your shared library" className="mt-3">
          <Select value={f.library_access} onValueChange={set("library_access")}>
            <SelectTrigger className="mt-1 h-9 w-full bg-white text-xs" data-testid="invite-access-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(ACCESS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`invite-access-${k}`}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <div className="mt-2 divide-y divide-zinc-200/70">
          <PermRow id="upload-app" label="Resonance storage" hint="Upload their own music to the app" checked={f.upload_app} onChange={set("upload_app")}>
            {f.upload_app && (
              <Select value={String(f.storage_limit_mb)} onValueChange={(v) => set("storage_limit_mb")(Number(v))}>
                <SelectTrigger className="h-7 w-24 bg-white text-[11px]" data-testid="invite-storage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STORAGE.map((mb) => <SelectItem key={mb} value={String(mb)} data-testid={`invite-storage-${mb}`}>{mb ? fmtMB(mb) : "Unlimited"}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </PermRow>
          <PermRow id="onedrive" label="Own OneDrive" hint="Connect and sync their own OneDrive music" checked={f.upload_onedrive} onChange={set("upload_onedrive")} />
          <PermRow id="plex" label="Own Plex server" hint="Connect their own Plex Media Server" checked={f.upload_plex} onChange={set("upload_plex")} />
          <PermRow id="filejump" label="FileJump songs" hint="See and play your FileJump collection in the shared library" checked={f.access_filejump} onChange={set("access_filejump")} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 md:col-span-2">
        <button onClick={() => create(true)} disabled={!!busy || !f.email || emailBad} data-testid="invite-email-button"
          className="flex h-9 items-center gap-1.5 rounded-full bg-zinc-900 px-4 text-xs font-medium text-white hover:bg-zinc-700 disabled:opacity-40">
          {busy === "mail" ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}Create & email invite
        </button>
        <button onClick={() => create(false)} disabled={!!busy} data-testid="invite-create-button"
          className="flex h-9 items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-4 text-xs font-medium text-zinc-800 hover:border-zinc-900 disabled:opacity-50">
          {busy === "copy" ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}Create & copy link
        </button>
        <span className="text-[11px] text-zinc-400">Email opens your own mail app with the link and a short message prefilled.</span>
      </div>
    </div>
  );
};

const InviteRow = ({ inv, senderName, onRevoke }) => {
  const perms = [inv.upload_app && (inv.storage_limit_mb ? fmtMB(inv.storage_limit_mb) : "storage"), inv.upload_onedrive && "OneDrive", inv.upload_plex && "Plex", inv.access_filejump && "FileJump"].filter(Boolean).join(" · ");
  return (
    <li className="flex flex-wrap items-center gap-3 py-2.5 text-xs" data-testid={`invite-${inv.token}`}>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-zinc-800">{inv.note || "Guest invite"}
          {inv.email && <span className="font-normal text-zinc-500" data-testid={`invite-email-${inv.token}`}> &lt;{inv.email}&gt;</span>}
          <span className="font-normal text-zinc-400"> · {ACCESS[inv.library_access]}{perms ? ` · ${perms}` : ""}</span>
        </p>
        <p className="truncate font-mono text-[10px] text-zinc-400">{linkFor(inv.token)}</p>
      </div>
      <span className="font-mono text-[10px] text-zinc-400">expires {fmtDate(inv.expires_at)}</span>
      <a href={mailtoFor(inv, senderName)} data-testid={`invite-mail-${inv.token}`} title={inv.email ? `Email ${inv.email}` : "Email the link"} className="rounded p-1.5 text-zinc-400 hover:text-zinc-900"><Mail size={14} /></a>
      <button onClick={() => copy(linkFor(inv.token), "Invite link copied")} data-testid={`invite-copy-${inv.token}`} title="Copy link" className="rounded p-1.5 text-zinc-400 hover:text-zinc-900"><Copy size={14} /></button>
      <button onClick={() => onRevoke(inv)} data-testid={`invite-revoke-${inv.token}`} title="Revoke" className="rounded p-1.5 text-zinc-400 hover:text-red-600"><Trash2 size={14} /></button>
    </li>
  );
};

export const InvitesSection = ({ senderName }) => {
  const [list, setList] = useState(null);
  const load = useCallback(() => api.get("/admin/invites").then((r) => setList(r.data)).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);
  const revoke = async (inv) => {
    if (!window.confirm(`Revoke the invite${inv.note ? ` for ${inv.note}` : ""}? The link stops working immediately.`)) return;
    try {
      await api.delete(`/admin/invites/${inv.token}`);
      setList((l) => l.filter((x) => x.token !== inv.token));
      toast.success("Invite revoked");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <section className="mb-8 rounded-lg border border-zinc-200 bg-white p-6" data-testid="invites-section">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-sky-50 text-sky-700"><Link2 size={18} /></div>
        <div>
          <h2 className="font-medium">Invite someone</h2>
          <p className="text-xs text-zinc-500">One-time links that work even when sign-ups are closed. Set the person's permissions here — they apply the moment they sign up.</p>
        </div>
      </div>
      <div className="mt-5"><NewInvite senderName={senderName} onCreated={(inv) => setList((l) => [inv, ...(l || [])])} /></div>
      {list === null ? <Loader2 className="mt-4 animate-spin text-zinc-400" /> : list.length > 0 && (
        <ul className="mt-4 divide-y divide-zinc-100" data-testid="invite-list">
          {list.map((inv) => <InviteRow key={inv.token} inv={inv} senderName={senderName} onRevoke={revoke} />)}
        </ul>
      )}
    </section>
  );
};
